import { faCheck, faCircleNotch, faClose } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { Badge } from 'primereact/badge'
import { Tooltip } from 'primereact/tooltip'
import { classNames } from 'primereact/utils'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectShowHideColumnProjectsOrderLine,
  updateShowHideColumnByTable,
} from '../../../../../features/projectManagementPmr/projectManagementPmrSlice'
import GMAWBUsersService from '../../../../../services/GMAWBUsersService'
import { E2EUserPaginatedModel } from '../../../../../shared/models/E2EUserPaginatedModel'
import { MailModel } from '../../../../../shared/models/Mail.model'
import { Nullable } from '../../../../../utils/helpers/ts.helpers'
import {
  projectDetailsOrderlneListColumnFlexHandler,
  projectDetailsOrderlneListColumnGmaWbHandler,
} from '../../../data/project-details-orderline-list-grid-columns'
import { HandlerEditState } from '../../../project-details-order-line-list/ProjectDetailsOrderLineList'
import HandlerType from '../../enums/handler-type'
import ProjectOrderlineStatus from '../../enums/project-orderline-status'
import { useProjectOrderLines } from '../../hooks/useProjectOrderLines'
import { useSaveHandler } from '../../hooks/useSaveHandler'
import PmrE2EProjectDetailsOrderlineModel, {
  HandlerAssignmentDraft,
  isOrderLineOngoing,
} from '../../interfaces/pmr-e2e-project-details-orderline-model'
import HandlerAssignmentNotificationEmailEditorDialog, {
  HandlerAssignmentNotificationEmailEditorDialogRef,
} from '../handler-assignment-notification-email-editor-dialog/HandlerAssignmentNotificationEmailEditorDialog'
import { useHandlerAssignmentNotificationEmailEditor } from '../handler-assignment-notification-email-editor-dialog/useHandlerAssignmentNotificationEmailEditor'
import ProjectCollaboratorAssignmentDropdown from '../project-collaborator-assignment-dialog/project-collaborator-assignment-dropdown/ProjectCollaboratorAssignmentDropdown'
import MailDraftIcon from './MailDraftIcon'
import classes from './ProjectSingleCollaboratorAvatarReassign.module.scss'
interface ProjectSingleCollaboratorAvatarReassignProps {
  id: string
  projectId: string
  orderLineDescription: string
  name: string
  email: string
  handlerType: HandlerType
  orderLineStatus: ProjectOrderlineStatus
  className?: string
  nonEditClassName?: string
  handlerEditState: Array<HandlerEditState>
  setHandlerEditState: React.Dispatch<React.SetStateAction<Array<HandlerEditState>>>
  updateHandlers: () => void
  removeDropdownAppendToSelf?: boolean
  isEmailEditable: boolean
  orderline: PmrE2EProjectDetailsOrderlineModel
}
const getInitials = (name: string) => {
  return name
    ?.split(',')
    ?.map(value => value.trim())
    ?.reverse()
    ?.map((value, index, array) =>
      index === 0 || index + 1 === array.length ? (value[0] ?? '').toUpperCase() : null
    )
    .join('')
}
const getHandlerClass = (handlerType: HandlerType) =>
  classes[HandlerType[handlerType]?.toLowerCase()]

const renderCollaboratorDisplayLabel = (handlerType: HandlerType): string | null => {
  if (handlerType === HandlerType.OrderCreator) return null

  switch (handlerType) {
    case HandlerType.FlexHandler:
      return 'FLEX Handler'
    case HandlerType.GmaWBHandler:
      return 'GMAWB Handler'
    case HandlerType.MilestoneHandler:
      return 'Milestone Handler'
    case HandlerType.TaskHandler:
      return 'Task Handler'
  }
}

interface ProjectUserSource {
  currentSkip: number
  currentTake: number
  currentSearchPhrase: string
  data: E2EUserPaginatedModel[]
}

const initDropdownValues: ProjectUserSource = {
  currentSkip: 0,
  currentTake: 5,
  currentSearchPhrase: '',
  data: [],
}

const ProjectSingleCollaboratorAvatarReassign = (
  props: ProjectSingleCollaboratorAvatarReassignProps
) => {
  const [handlerDropdownOptions, setHandlerDropdownOptions] =
    useState<ProjectUserSource>(initDropdownValues)
  const [currentHandler, setCurrentHandler] = useState<E2EUserPaginatedModel>()
  const [isDropdownLoading, setIsDropdownLoading] = useState<boolean>(true)
  const selectOrderLineListGridColumns = useSelector(selectShowHideColumnProjectsOrderLine)
  const dispatch = useDispatch()
  const emailEditorRef = useRef<HandlerAssignmentNotificationEmailEditorDialogRef | null>(null)
  const {
    emailEditorVisible,
    openEmailEditor,
    closeEmailEditor,
    emailEditorOrderlines,
    showCancelToastNotification,
    showSaveAndCloseToastNotification,
    setEmailEditorVisible,
  } = useHandlerAssignmentNotificationEmailEditor({
    onSuppressed: () => onConfirm(),
    emailEditorRef,
  })
  const { activeProjectOrderLines } = useProjectOrderLines(props.projectId)
  const { isSavingHandler, saveHandler } = useSaveHandler({
    onSuccess: () => {
      const lastEditStateCount = updateHandlerState(false)
      if (lastEditStateCount === 0) applyColumnAdjustOnEditHandler(lastEditStateCount)
      setIsOpenedFromDraft(false)
      props.updateHandlers()
    },
  })
  const [isOpenedFromDraft, setIsOpenedFromDraft] = useState<boolean>(false)

  const isOnEditState = props.handlerEditState.some(handlerEdit => {
    if (props.id === handlerEdit.orderLineId) {
      if (props.handlerType === HandlerType.FlexHandler) return handlerEdit.isFlexHandlerEdit
      if (props.handlerType === HandlerType.GmaWBHandler) return handlerEdit.isGmawbHandlerEdit
    }

    return false
  })

  const isHandlerChanged = currentHandler?.displayName !== props.name

  useEffect(() => {
    if (!isOnEditState) return

    setIsDropdownLoading(true)
    GMAWBUsersService.getPaginatedUsers(0, 5, props.handlerType, false, '').then(response => {
      let flexData: E2EUserPaginatedModel[] = structuredClone(response?.data)
      if (flexData?.some(data => data.displayName === props.email)) {
        flexData = flexData?.filter(data => data.normalizedEmail !== props.email)
      }
      flexData?.unshift({
        displayName: props.name,
        normalizedEmail: props.email,
      })

      setHandlerDropdownOptions(current => ({
        ...current,
        data: flexData,
      }))

      setCurrentHandler(current => ({
        ...current,
        normalizedEmail: props.email,
        displayName: props.name,
      }))

      setIsDropdownLoading(false)
    })
  }, [isOnEditState])

  const onHandlerClick = () => {
    if (props.orderLineStatus === ProjectOrderlineStatus.Completed) return

    const lastEditStateCount = updateHandlerState(true)
    applyColumnAdjustOnEditHandler(lastEditStateCount)
  }

  const onConfirm = async (customMail?: MailModel) => {
    if (!isOpenedFromDraft) closeEmailEditor()
    if (!currentHandler) return

    await saveHandler(
      customMail ? emailEditorOrderlines : [props.orderline],
      props.handlerType,
      currentHandler,
      customMail
    )

    closeEmailEditor()
  }

  const onCancel = () => {
    setCurrentHandler(current => ({
      ...current,
      normalizedEmail: props.email,
      displayName: props.name,
    }))

    const lastEditStateCount = updateHandlerState(false)
    if (lastEditStateCount === 0) applyColumnAdjustOnEditHandler(lastEditStateCount)
  }

  const updateHandlerState = (isEdit: boolean): number => {
    const updatedHandlerState = props.handlerEditState.map(handlerState => {
      if (props.id === handlerState.orderLineId) {
        if (props.handlerType === HandlerType.FlexHandler) handlerState.isFlexHandlerEdit = isEdit

        if (props.handlerType === HandlerType.GmaWBHandler) handlerState.isGmawbHandlerEdit = isEdit
      }

      return handlerState
    })

    props.setHandlerEditState(updatedHandlerState)

    const lastEditStateCount = updatedHandlerState.filter(handlerState => {
      if (props.handlerType === HandlerType.FlexHandler)
        return handlerState.isFlexHandlerEdit === true
      if (props.handlerType === HandlerType.GmaWBHandler)
        return handlerState.isGmawbHandlerEdit === true

      return []
    }).length

    return lastEditStateCount
  }

  const applyColumnAdjustOnEditHandler = (lastEditStateCount: number) => {
    const showHideCopyOrderLineList = structuredClone(selectOrderLineListGridColumns)
    const defaultWidth: number = 200
    const minWidthOnEdit: number = 320

    showHideCopyOrderLineList?.fields.map(field => {
      if (
        (props.handlerType === HandlerType.FlexHandler &&
          field.id === projectDetailsOrderlneListColumnFlexHandler) ||
        (props.handlerType === HandlerType.GmaWBHandler &&
          field.id === projectDetailsOrderlneListColumnGmaWbHandler)
      ) {
        const hasOtherEdit = lastEditStateCount >= 1
        const resolvedWidth = hasOtherEdit ? minWidthOnEdit : defaultWidth

        if (lastEditStateCount <= 1) {
          field.width = resolvedWidth + 'px'
          field.widthNum = resolvedWidth
        }
      }

      return field
    })

    dispatch(
      updateShowHideColumnByTable({
        key: showHideCopyOrderLineList?.key,
        newColumns: showHideCopyOrderLineList?.fields,
        frozenCount: showHideCopyOrderLineList?.frozenColumnCount,
      })
    )
  }

  const handleEmailEditingCancel = (draftId: Nullable<string>, isFromSaveAndClose?: boolean) => {
    closeEmailEditor()

    if (isFromSaveAndClose) showSaveAndCloseToastNotification()
    else if (!draftId) showCancelToastNotification()

    onCancel()
  }

  const constructComponentUniqueClassName = (prefix: string) => {
    return `${prefix}_${renderCollaboratorDisplayLabel(props.handlerType)?.replace(/\s/g, '')}-${props.id}`
  }

  const isEditDisabled = props.isEmailEditable && !!props.orderline.handlerAssignmentDraft

  const handleCheckClick = () => {
    if (props.isEmailEditable) {
      openEmailEditor([props.orderline], currentHandler!)
      setIsOpenedFromDraft(true)
      return
    }

    onConfirm()
  }

  return (
    <>
      {props.name && !isOnEditState && (
        <div
          className={clsx(classes['badge-container'], '!p-0', {
            'gmawb-clickable-ui': isOrderLineOngoing(props.orderLineStatus),
          })}
        >
          {
            <>
              <Tooltip
                autoHide={true}
                position="right"
                className={`${classes['tooltip']}`}
                target={constructComponentUniqueClassName('.badge-tooltip')}
              >
                <div className={`${classes['tooltip-displayName']}`}>{props.name}</div>
                <div className={`${classes['tooltip-handlerLabel']}`}>
                  {renderCollaboratorDisplayLabel(props.handlerType)}
                </div>
              </Tooltip>
              <span
                onClick={() => !isEditDisabled && onHandlerClick()}
                className={classNames(isEditDisabled && 'opacity-50')}
              >
                <Badge
                  value={getInitials(props.name)}
                  className={clsx(
                    classes['badge'],
                    getHandlerClass(props.handlerType),
                    {
                      [classes['badge-editable']]:
                        props.orderLineStatus !== ProjectOrderlineStatus.Completed &&
                        !isEditDisabled,
                    },
                    constructComponentUniqueClassName('badge-tooltip')
                  )}
                  size="large"
                ></Badge>
              </span>
            </>
          }
        </div>
      )}

      {!isOnEditState && !!props.orderline.handlerAssignmentDraft && props.isEmailEditable && (
        <MailDraftIcon
          id={props.orderline.handlerAssignmentDraft!.id}
          className={classes['tooltip']}
          tooltipTarget={constructComponentUniqueClassName('draft-tooltip')}
          onResume={(draft: HandlerAssignmentDraft) => {
            const newHandler = props.orderline.handlerAssignmentDraft!.newHandlerInformation
            setCurrentHandler(newHandler)

            const draftProjectLines = activeProjectOrderLines().filter(
              line =>
                line.handlerAssignmentDraft?.mailId ===
                props.orderline.handlerAssignmentDraft!.mailId
            )
            setIsOpenedFromDraft(true)
            openEmailEditor(draftProjectLines, newHandler, draft.mail)
          }}
        />
      )}

      {isOnEditState && (
        <div className={`${props.className} ${classes['edit-collaborator-container']}`}>
          {isDropdownLoading && <FontAwesomeIcon icon={faCircleNotch} spin={true} />}

          {!isDropdownLoading && (
            <>
              <ProjectCollaboratorAssignmentDropdown
                value={currentHandler}
                setValue={setCurrentHandler}
                dropdownOptions={handlerDropdownOptions}
                setDropdownOptions={setHandlerDropdownOptions}
                handlerType={HandlerType.FlexHandler}
                isGmaTcTask={false}
                isDisabled={isSavingHandler}
                removeAppendToSelf={props.removeDropdownAppendToSelf}
              />
              <div className={classes['single-handler-reassignment-container']}>
                <FontAwesomeIcon
                  onClick={isSavingHandler || !isHandlerChanged ? undefined : handleCheckClick}
                  className={clsx(
                    classes['confirm-button'],
                    { [classes['cancel-button-padding']]: isSavingHandler || !isHandlerChanged },
                    { [classes['action-buttons-disabled']]: isSavingHandler || !isHandlerChanged },
                    { 'gmawb-clickable-ui': !isSavingHandler && isHandlerChanged }
                  )}
                  icon={isSavingHandler ? faCircleNotch : faCheck}
                  spin={isSavingHandler}
                  opacity={isSavingHandler || !isHandlerChanged ? 0.5 : 1.0}
                />
                <FontAwesomeIcon
                  onClick={isSavingHandler ? undefined : onCancel}
                  className={clsx(
                    classes['cancel-button'],
                    { [classes['cancel-button-padding']]: isSavingHandler },
                    { [classes['action-buttons-disabled']]: isSavingHandler },
                    { 'gmawb-clickable-ui': !isSavingHandler }
                  )}
                  icon={faClose}
                  opacity={isSavingHandler ? 0.5 : 1.0}
                />
              </div>
            </>
          )}
        </div>
      )}
      <HandlerAssignmentNotificationEmailEditorDialog
        visible={emailEditorVisible}
        onHide={handleEmailEditingCancel}
        selectedNewHandler={currentHandler}
        orderlines={emailEditorOrderlines}
        onSend={onConfirm}
        ref={emailEditorRef}
        onMinimize={() => {
          setEmailEditorVisible(false)
          onCancel()
        }}
        handlerType={props.handlerType}
        isSending={isSavingHandler}
      />
    </>
  )
}
export default ProjectSingleCollaboratorAvatarReassign
